<template>
  <app-page :title="$t('payschedules.title')" :subtitle="$t('payschedules.subtitle')" icon="money-check-alt">
    <template v-slot:infobox>
      <sme-info-box :title="$t('payschedules.faq.whatIsTitle')" id="about-pay-schedules" save-state>
        <p>
          {{ $t('payschedules.faq.whatIsBody1') }}
        </p>
        <ul>
          <li>{{ $t('payschedules.faq.whatIsBullet1') }}</li>
          <li>{{ $t('payschedules.faq.whatIsBullet2') }}</li>
        </ul>
        <template v-if="!isDisablePayscheduleEditing">
          <p>
            <i18n-t path="payschedules.faq.createBody" tag="span">
              <template #addButton
                ><strong>{{ $t('payschedules.faq.createBodyBold') }}</strong></template
              >
            </i18n-t>
          </p>
          <p>
            {{ $t('payschedules.faq.keepUpToDateBody') }}
          </p>
        </template>
      </sme-info-box>

      <sme-info-box
        :title="$t('payschedules.faq.payrollTitle')"
        id="about-payroll"
        save-state
        v-if="!hideStreamAccountRelatedItems"
      >
        <p>
          {{ $t('payschedules.faq.payrollBody1') }}
        </p>
        <p>
          <strong>{{ $t('payschedules.faq.payrollBody2Bold') }}</strong
          >.
        </p>
        <p>
          <i18n-t path="payschedules.faq.payrollBody3" tag="span">
            <template #potLabel>{{ bankingInfo.save.potLabel }}</template>
          </i18n-t>
        </p>

        <p>
          <i18n-t path="payschedules.faq.payrollBody4" tag="span">
            <template #integrationsLink
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('payschedules.faq.integrationsLinkText')
              }}</router-link></template
            >
          </i18n-t>
        </p>
      </sme-info-box>
    </template>

    <template v-slot:header>
      <b-button :to="{ name: 'sme-bank-pay' }" variant="primary" v-if="isEnableBankPayHistory">
        <font-awesome-icon :icon="['fad', 'calendar-days']" class="mr-2" />{{ $t('payschedules.bankPaymentsButton') }}
      </b-button>
      <b-button :to="{ name: 'sme-off-cycle-payments' }" variant="primary" v-if="showOffCyclePayments">
        <font-awesome-icon :icon="['far', 'coins']" class="mr-2" />{{ $t('payschedules.offCyclePaymentsButton') }}
      </b-button>
      <span
        v-if="showPayWithStream"
        v-b-tooltip.top
        :title="!isBankingSetup && $t('payschedules.payWithStreamTooltip')"
        :disabled="isBankingSetup"
      >
        <b-button :to="{ name: 'sme-payment-run' }" variant="primary" :disabled="!isBankingSetup">
          <font-awesome-icon :icon="['fal', 'upload']" class="mr-2" />{{ $t('payschedules.payWithStreamButton') }}
        </b-button>
      </span>
    </template>

    <template v-slot:custom-content>
      <b-col>
        <div class="scroll-pills" :class="{ 'scroll-pills--stuck': isStuck }" ref="pillsRef">
          <button
            v-for="pill in visiblePills"
            :key="pill.id"
            class="scroll-pill"
            :class="{ 'scroll-pill--active': activeSection === pill.id }"
            @click="scrollToSection(pill.id)"
          >
            {{ pill.label }}
          </button>
        </div>

        <div id="pay-section-schedules">
          <pay-schedules-table />
        </div>
        <div id="pay-section-payroll" class="mt-4" v-if="!hideStreamAccountRelatedItems">
          <pay-schedules-yet-to-be-paid />
        </div>
        <div id="pay-section-failed" class="mt-4" v-if="!hideStreamAccountRelatedItems">
          <pay-schedules-failed-salaries />
        </div>
        <div id="pay-section-missed" class="mt-4" v-if="!hideStreamAccountRelatedItems">
          <pay-schedules-missed-salaries />
        </div>
        <div id="pay-section-returned" class="mt-4" v-if="!hideStreamAccountRelatedItems">
          <pay-schedules-returned-salaries />
        </div>
        <div id="pay-section-invalid" class="mt-4" v-if="!hideStreamAccountRelatedItems">
          <pay-schedules-invalid-bank-details />
        </div>
      </b-col>
    </template>
  </app-page>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { getBankingInfo } from '@/Banking';
import AppPage from '@/components/AppPage';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import i18n from '@/i18n';
import PaySchedulesFailedSalaries from '@/pages/payschedules/components/PaySchedulesFailedSalaries.vue';
import PaySchedulesMissedSalaries from '@/pages/payschedules/components/PaySchedulesMissedSalaries.vue';
import PaySchedulesReturnedSalaries from '@/pages/payschedules/components/PaySchedulesReturnedSalaries.vue';
import PaySchedulesTable from '@/pages/payschedules/components/PaySchedulesTable.vue';
import PaySchedulesYetToBePaid from '@/pages/payschedules/components/PaySchedulesYetToBePaid.vue';
import PaySchedulesInvalidBankDetails from '@/pages/payschedules/components/PaySchedulesInvalidBankDetails.vue';
import State from '@/state/State';
import usePayschedulesReport from '@/state/composables/usePayschedulesReport';

const { getPayschedulesReport } = usePayschedulesReport();
const {
  isDisablePayscheduleEditing,
  isDisablePayWithWagestream,
  isDisableOffCyclePayments,
  isEnableBankPayHistory,
  hideStreamAccountRelatedItems,
  offCyclePaymentsVersion,
  isUkCompany,
} = useFeatureFlags();

const showOffCyclePayments = computed(
  () => offCyclePaymentsVersion.value === 'off' && !isDisableOffCyclePayments.value,
);

// Pay with Stream is a UK-only self-serve payment run — hide for non-UK.
const showPayWithStream = computed(() => !isDisablePayWithWagestream.value && isUkCompany.value);

const startDate = new Date();
startDate.setDate(startDate.getDate() - 35);
const START_DATE = startDate.toISOString().split('T')[0];
const END_DATE = new Date().toISOString().split('T')[0];

const isBankingSetup = computed(() => !!State.state.company.properties?.modulr);
const bankingInfo = getBankingInfo();

getPayschedulesReport(START_DATE, END_DATE);

// Scroll pills
const pillsRef = ref(null);
const isStuck = ref(false);
const activeSection = ref(null);

// computed so that i18n.t() runs on every locale switch, and labels reuse each tile's own
// $t('...title') so the pill mirrors the section it scrolls to
const allPills = computed(() => [
  { id: 'pay-section-schedules', label: i18n.t('payschedules.table.title'), condition: () => true },
  {
    id: 'pay-section-payroll',
    label: i18n.t('payschedules.yetToBePaid.title'),
    condition: () => !hideStreamAccountRelatedItems.value,
  },
  {
    id: 'pay-section-failed',
    label: i18n.t('payschedules.failedSalaries.title'),
    condition: () => !hideStreamAccountRelatedItems.value,
  },
  {
    id: 'pay-section-missed',
    label: i18n.t('payschedules.missedSalaries.title'),
    condition: () => !hideStreamAccountRelatedItems.value,
  },
  {
    id: 'pay-section-returned',
    label: i18n.t('payschedules.returnedSalaries.title'),
    condition: () => !hideStreamAccountRelatedItems.value,
  },
  {
    id: 'pay-section-invalid',
    label: i18n.t('payschedules.invalidBankDetails.title'),
    condition: () => !hideStreamAccountRelatedItems.value,
  },
]);

const visiblePills = computed(() => allPills.value.filter(p => p.condition()));

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) {
    return;
  }
  const offset = 60;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
}

let observer;
let stuckObserver;

onMounted(() => {
  const sectionIds = allPills.value.map(p => p.id);
  observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeSection.value = entry.target.id;
        }
      }
    },
    { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
  );

  sectionIds.forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      observer.observe(el);
    }
  });

  if (pillsRef.value) {
    stuckObserver = new IntersectionObserver(
      ([entry]) => {
        isStuck.value = !entry.isIntersecting;
      },
      { threshold: 1 },
    );
    stuckObserver.observe(pillsRef.value);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
  if (stuckObserver) {
    stuckObserver.disconnect();
  }
});
</script>

<style scoped>
.scroll-pills {
  display: none;
}

@media (max-width: 767.98px) {
  .scroll-pills {
    display: flex;
    gap: 0.5rem;
    padding: 0 0;
    margin-top: 0.25rem;
    margin-bottom: 1.75rem;
    position: sticky;
    top: 0;
    z-index: 100;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .scroll-pills::-webkit-scrollbar {
    display: none;
  }

  .scroll-pills--stuck {
    background-color: var(--palette-color-base-white);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 0.5rem 1rem;
    margin-left: -15px;
    margin-right: -15px;
  }
}

.scroll-pill {
  flex-shrink: 0;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  border: 1px solid var(--palette-color-default-lighten-80);
  background: var(--palette-color-base-white);
  color: var(--palette-color-default);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  line-height: 1.4;
}

.scroll-pill:hover {
  border-color: var(--palette-color-brand-primary);
  color: var(--palette-color-brand-primary);
  background: var(--palette-color-core-1-lighten-90);
}

.scroll-pill--active {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
  border-color: var(--palette-color-brand-primary);
}

.scroll-pill--active:hover {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
}
</style>
